// src/services/googleAuthService.ts - UPDATED with Firestore integration
import { User } from 'firebase/auth';
import API_URLS from '../component/api/urls';
import { FirestoreService } from './firestoreService';

interface GoogleLoginData {
  type: "email";
  email: string;
  is_google: boolean;
  google_id?: string;
  name?: string;
  profile_picture?: string;
}

interface LoginResponseData {
  Success: string;
  message: string;
  user_id: number;
  otp?: number;
  random_gen_no?: string;
  user_status: string;
  remaining_days: number;
  access_token: string;
  refresh_token: string;
  contactInfo: string;
  contactType: "email";
  isNewUser?: boolean;
  isGoogleAuthenticated?: boolean;
  user_data?: {
    user_id: number;
    user_mobileno: string;
    otp: string;
    user_fname: string;
    user_lname: string;
    goal: string;
    gender: string;
    age: number;
    city: string;
    state: string;
    dob: string;
    height_parameter: string;
    height_value: number;
    weight_parameter: string;
    weight_value: number;
    target_weight: number;
    target_weight_parameter: string;
    food_preference: string;
    progress: string;
    medical_conditions: string;
    last_status: string | null;
    profile_url: string;
  };
}

interface UpdateUserNameResponse {
  success: string;
  message: string;
  user_data?: any;
}

export class GoogleAuthService {
  
  /**
   * Process Google user data with Firestore sync
   */
  static async loginWithGoogleUser(googleUser: User): Promise<LoginResponseData> {
    try {
      const userInfo = GoogleAuthService.extractUserInfo(googleUser);
      
      const googleLoginData: GoogleLoginData = {
        type: "email",
        email: googleUser.email || '',
        is_google: true,
        google_id: googleUser.uid,
        name: googleUser.displayName || '',
        profile_picture: googleUser.photoURL || undefined
      };

      console.log("=== Google Login API Call ===");
      console.log("Sending data to USER_LOGIN:", googleLoginData);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const response = await fetch(API_URLS.USER_LOGIN, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(googleLoginData),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          if (response.status === 408 || response.status === 504) {
            throw new Error("Request timed out. Please check your connection and try again.");
          }
          throw new Error(`Server error (${response.status}). Please try again.`);
        }

        const result = await response.json();
        console.log("=== Google Login API Response ===");
        console.log("Response data:", result);

        if (result.Success === "1") {
          const isNewUser = this.determineIfNewUser(result, userInfo);
          
          const loginResponseData: LoginResponseData = {
            ...result,
            contactInfo: googleUser.email || '',
            contactType: "email" as const,
            isNewUser: isNewUser,
            isGoogleAuthenticated: true,
            otp: undefined
          };

          console.log("=== Processed Login Response ===");
          console.log("isNewUser:", isNewUser);
          console.log("isGoogleAuthenticated:", true);

          // ✅ NEW: Sync user data to Firestore
          await this.syncGoogleUserToFirestore(
            googleUser,
            result.user_id.toString(),
            isNewUser,
            result.user_data
          );

          return loginResponseData;
        } else {
          throw new Error(result.message || "Google login failed");
        }

      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error("Request timed out. Please check your connection and try again.");
        }
        
        throw fetchError;
      }

    } catch (error: any) {
      console.error("Google login API error:", error);
      
      let userMessage = error.message;
      
      if (error.message?.includes('fetch')) {
        userMessage = "Network error. Please check your connection and try again.";
      } else if (error.message?.includes('timeout')) {
        userMessage = "Request timed out. Please try again.";
      } else if (!error.message || error.message.includes('undefined')) {
        userMessage = "Google login failed. Please try again.";
      }
      
      throw new Error(userMessage);
    }
  }

  /**
   * ✅ NEW: Sync Google user data to Firestore
   */
  static async syncGoogleUserToFirestore(
    googleUser: User,
    userID: string,
    isNewUser: boolean,
    backendUserData?: any
  ): Promise<void> {
    try {
      console.log("=== Syncing User to Firestore ===");
      console.log("User ID:", userID);
      console.log("Is New User:", isNewUser);

      const userInfo = this.extractUserInfo(googleUser);
      
      if (isNewUser) {
        // Create new user document in Firestore
        await FirestoreService.createGoogleUser(
          userID,
          googleUser.email || '',
          googleUser.displayName || userInfo.firstName || 'User',
          '', // Phone number - will be updated later if available
          googleUser.photoURL || '',
          '' // FCM token - will be set when implementing push notifications
        );
        console.log("✅ New Google user created in Firestore");
      } else {
        // Update existing user document
        const updateData: any = {
          email: googleUser.email || '',
          photoURL: googleUser.photoURL || '',
          status: "Unavailable", // or "Available" based on your logic
        };

        // If we have backend user data, sync it
        if (backendUserData) {
          if (backendUserData.user_fname || backendUserData.user_lname) {
            updateData.name = `${backendUserData.user_fname || ''} ${backendUserData.user_lname || ''}`.trim();
            updateData.firstName = backendUserData.user_fname || '';
            updateData.lastName = backendUserData.user_lname || '';
          }
          
          if (backendUserData.user_mobileno) {
            updateData.phone = backendUserData.user_mobileno;
          }
        } else if (googleUser.displayName) {
          updateData.name = googleUser.displayName;
          updateData.firstName = userInfo.firstName;
          updateData.lastName = userInfo.lastName;
        }

        await FirestoreService.setUserData(userID, updateData);
        console.log("✅ Existing user updated in Firestore");
      }

    } catch (error: any) {
      console.error("❌ Failed to sync user to Firestore:", error);
      // Don't throw error - Firestore sync failure shouldn't block login
      // But log it for debugging
      console.warn("⚠️ Continuing login despite Firestore sync failure");
    }
  }

  /**
   * Determine if user is new based on API response
   */
  private static determineIfNewUser(apiResult: any, googleUserInfo: any): boolean {
    if (apiResult.user_status === 'new' || apiResult.isNewUser === true) {
      return true;
    }
    
    if (apiResult.user_data) {
      const userData = apiResult.user_data;
      if (!userData.user_fname || userData.user_fname.trim() === '') {
        return true;
      }
    }
    
    if (apiResult.remaining_days === 0 && !apiResult.user_data?.user_fname) {
      return true;
    }
    
    return false;
  }

  /**
   * Update user name in both backend and Firestore
   */
  static async updateUserName(
    userId: number, 
    firstName: string, 
    lastName: string, 
    accessToken: string
  ): Promise<UpdateUserNameResponse> {
    try {
      console.log("=== Update User Name (Backend + Firestore) ===");
      console.log("User ID:", userId);
      console.log("First Name:", firstName);
      console.log("Last Name:", lastName);

      // Update backend
      const urlParams = new URLSearchParams();
      urlParams.append('user_fname', firstName);
      urlParams.append('user_lname', lastName);

      const apiUrl = `${API_URLS.UPDATE_USER_NAME}${userId}/`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json',
          },
          body: urlParams.toString(),
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        const responseText = await response.text();
        console.log("Backend update response:", responseText);

        let result: UpdateUserNameResponse | null = null;
        
        if (responseText) {
          try {
            result = JSON.parse(responseText);
          } catch (parseError) {
            throw new Error(`Invalid server response: ${responseText}`);
          }
        }

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("Authentication failed. Please login again.");
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        if (result && result.success === "1") {
          console.log("✅ Backend: User name updated successfully");
          
          // ✅ NEW: Update Firestore as well
          try {
            await FirestoreService.updateUserName(
              userId.toString(),
              firstName,
              lastName
            );
            console.log("✅ Firestore: User name updated successfully");
          } catch (firestoreError) {
            console.error("⚠️ Firestore name update failed:", firestoreError);
            // Don't throw - backend update succeeded
          }

          return result;
        } else {
          throw new Error(result?.message || "Failed to update user name");
        }

      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error("Request timed out while updating name.");
        }
        
        throw fetchError;
      }

    } catch (error: any) {
      console.error("Update user name error:", error);
      throw error;
    }
  }

  /**
   * ✅ NEW: Update user phone in Firestore
   */
  static async updateUserPhone(
    userId: string,
    phone: string
  ): Promise<void> {
    try {
      await FirestoreService.updateUserPhone(userId, phone);
    } catch (error) {
      console.error("Failed to update phone in Firestore:", error);
    }
  }

  /**
   * ✅ NEW: Update user status in Firestore
   */
  static async updateUserStatus(
    userId: string,
    status: "Available" | "Unavailable"
  ): Promise<void> {
    try {
      await FirestoreService.updateUserStatus(userId, status);
    } catch (error) {
      console.error("Failed to update status in Firestore:", error);
    }
  }

  /**
   * ✅ NEW: Update FCM token for push notifications
   */
  static async updateFCMToken(
    userId: string,
    fcmToken: string
  ): Promise<void> {
    try {
      await FirestoreService.updateFCMToken(userId, fcmToken);
    } catch (error) {
      console.error("Failed to update FCM token in Firestore:", error);
    }
  }

  /**
   * Extract user information from Google user object
   */
  static extractUserInfo(googleUser: User) {
    const displayName = googleUser.displayName || '';
    const nameParts = displayName.split(' ');
    
    return {
      uid: googleUser.uid,
      email: googleUser.email,
      displayName: displayName,
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      photoURL: googleUser.photoURL,
      emailVerified: googleUser.emailVerified
    };
  }

  /**
   * Validate Google user data
   */
  static validateGoogleUser(googleUser: User): boolean {
    if (!googleUser.email) {
      console.error("Google user has no email");
      return false;
    }

    if (!googleUser.uid) {
      console.error("Google user has no UID");
      return false;
    }

    return true;
  }

  /**
   * Check if Google user has a complete name
   */
  static hasCompleteName(googleUser: User): boolean {
    const userInfo = this.extractUserInfo(googleUser);
    return !!(userInfo.firstName && userInfo.firstName.trim().length > 0);
  }
}